<template>
  <div
    class="af"
    :class="{ 'is-deleting': isDeleting, 'is-selected': isSelected }"
  >
    <!-- Thumbnail -->
    <div class="af-thumb">
      <Popover v-if="isImage" :triggers="['hover']" placement="left">
        <div
          :class="{ selectable: isSelectable, 'is-selected': isSelected }"
          @click="!isDeleting && $emit('select', $event)"
        >
          <figure>
            <img :src="thumbnailUrl || directUrl" />
          </figure>
          <div class="checkmark"><i class="fa fa-check" /></div>
        </div>
        <template #popper>
          <span v-if="!showThumb" class="thumb-preview is-loading">
            <i class="fa fa-spinner spinner" />
          </span>
          <img
            v-show="showThumb"
            class="thumb-preview"
            :src="directUrl"
            :alt="name"
            @load="showThumb = true"
          />
        </template>
      </Popover>
      <div
        v-else
        :class="{ selectable: isSelectable, 'is-selected': isSelected }"
        @click="!isDeleting && $emit('select', $event)"
      >
        <figure>
          <span class="icon is-big"><i :class="iconClasses" /></span>
        </figure>
        <div class="checkmark"><i class="fa fa-check" /></div>
      </div>
    </div>

    <!-- Name + metadata -->
    <div class="af-info">
      <div class="af-name">
        <IconButton
          icon="fa-solid fa-link"
          size="small"
          tooltip="Copy file URL"
          colorful
          :show-arrow="false"
          @click="handleCopyLink"
        />
        <router-link
          v-if="handledByLool && wopiTokenUrl"
          class="has-text-weight-bold"
          :to="{ name: 'officeApp', params: { id } }"
        >
          {{ name }}
        </router-link>
        <a
          v-else
          class="has-text-weight-bold"
          :href="directUrl"
          target="_blank"
          rel="noopener"
        >
          {{ name }}
        </a>
      </div>
      <div class="af-meta">
        <template v-if="!hasBeenModifiedAfterCreation">
          Uploaded by
          <router-link :to="userRoute(createdBy)">{{ createdBy }}</router-link>
          on {{ readableCreated }}
        </template>
        <template v-else>
          Modified by
          <router-link :to="userRoute(modifiedBy)">
            {{ modifiedBy }}
          </router-link>
          on {{ readableModified }}
        </template>
        <!-- Timestamp status -->
        <template v-if="showTimestamp">
          <a v-if="tsrUrl" :href="tsrUrl" target="_blank" rel="noopener">
            <span
              v-tooltip="
                'Digitally timestamped — click to download certificate'
              "
              class="icon hoverable-icon is-small has-text-success"
            >
              <i class="fa-solid fa-certificate" />
            </span>
          </a>
          <span
            v-else-if="isWithinTimeStampGenerationDelta"
            v-tooltip="'Timestamp is being generated…'"
            class="icon is-small"
          >
            <i class="fa-solid fa-circle-notch fa-spin" />
          </span>
          <span
            v-else
            v-tooltip="timestampErrorTooltip"
            class="icon is-small has-text-warning fa-stack"
            style="opacity: 0.5"
          >
            <i class="fa-solid fa-certificate fa-stack-1x" />
            <i class="has-text-danger fa-solid fa-exclamation fa-stack-1x" />
          </span>
        </template>
      </div>
    </div>

    <!-- Size -->
    <span class="af-size">{{ size }}</span>

    <!-- Actions -->
    <div class="af-actions">
      <IconButton
        v-if="preview"
        icon="fa-solid fa-eye"
        tooltip="Preview"
        size="small"
        colorful
        :show-arrow="false"
        @click="openPreview = !isDeleting"
      />
      <IconButton
        icon="fa-solid fa-download"
        tooltip="Download"
        size="small"
        colorful
        :show-arrow="false"
        @click="!isDeleting && $emit('download')"
      />
      <span v-if="isEmbedding" class="icon is-small">
        <i class="fa-solid fa-spinner spinner" />
      </span>
      <IconButton
        v-else-if="canEmbed && isEmbeddable"
        icon="fa-solid fa-image"
        tooltip="Embed in text at cursor position"
        size="small"
        colorful
        :show-arrow="false"
        @click="!isDeleting && $emit('embed')"
      />
      <span
        v-else-if="isEmbedded && isObsoleteEmbed"
        v-tooltip="'Image is not embedded in text anymore'"
        class="icon is-small hoverable-icon has-text-danger"
      >
        <i class="fa-solid fa-broom" />
      </span>
      <span v-if="isDeleting" class="icon is-small has-text-danger">
        <i class="fa-solid fa-spinner spinner" />
      </span>
      <IconButton
        v-else-if="canDelete"
        icon="fa-solid fa-trash"
        color="danger"
        tooltip="Delete"
        size="small"
        colorful
        :show-arrow="false"
        align="end"
      >
        <template #dropdown>
          <div class="af-confirm">
            <p>
              <code>{{ name }}</code>
              will be
              <strong class="is-underlined">permanently</strong>
              deleted
            </p>
            <button
              class="button is-danger is-small has-text-weight-bold"
              @click="$emit('delete')"
            >
              OK
            </button>
          </div>
        </template>
      </IconButton>
    </div>

    <!-- Preview modal -->
    <ModalCard
      v-if="openPreview"
      :title="`Preview of ${name}`"
      :handle-close="() => (openPreview = false)"
      width="80%"
    >
      <template #body>
        <pre class="af-preview-body">{{ preview }}</pre>
      </template>
      <template #footer>
        <button
          class="button is-primary is-small is-outlined ml-auto"
          @click="$emit('download')"
        >
          <span class="icon"><i class="fa-solid fa-download" /></span>
          <span>Download</span>
        </button>
      </template>
    </ModalCard>
  </div>
</template>

<script>
import { useClipboard } from '@vueuse/core'
import { unref } from 'vue'

import { LOOL_MIMETYPES } from '@/utils'

export default {
  emits: ['select', 'download', 'embed', 'delete'],
  props: {
    attachedFile: { type: Object, default: () => ({}) },
    isSelectable: { type: Boolean, default: false },
    isSelected: { type: Boolean, default: false },
    canEmbed: { type: Boolean, default: false },
    isEmbedding: { type: Boolean, default: false },
    canDelete: { type: Boolean, default: true },
    isDeleting: { type: Boolean, default: false },
    showTimestamp: { type: Boolean, default: true },
  },
  setup(props) {
    const { copy } = useClipboard()
    const f = unref(props.attachedFile)
    return {
      ...f,
      isObsoleteEmbed: f.isObsoleteEmbed || false,
      canRetryTimestamp: !f.isAutogenerated && !f.isEmbedded,
      readableCreated: f.prettyDates.created,
      readableModified: f.prettyDates.modified,
      handledByLool: LOOL_MIMETYPES.includes(f.mimeType),
      isImage: f.iconClasses.includes('fa-file-image'),
      copy,
    }
  },
  data() {
    return { openPreview: false, showThumb: false }
  },
  computed: {
    clipboardUrl() {
      return this.wopiTokenUrl
        ? window.location.origin +
            this.$router.resolve({
              name: 'officeApp',
              params: { id: this.id },
            }).href
        : this.directUrl
    },
    timestampErrorTooltip() {
      return {
        html: true,
        content: [
          'An issue occurred during the timestamp generation process',
          this.canRetryTimestamp && 'Try re-uploading the file',
        ]
          .filter(Boolean)
          .join('<br/>'),
      }
    },
  },
  methods: {
    handleCopyLink() {
      this.copy(this.clipboardUrl)
      this.$store.commit('notify', {
        type: 'success',
        message: 'Copied URL to clipboard.',
      })
    },
    userRoute(username) {
      return { name: 'userDetail', params: { userUsername: username } }
    },
  },
}
</script>

<style lang="scss" scoped>
/* ── Grid layout with container queries ──────────────── */
.af {
  --thumb-size: 42px;

  container-type: inline-size;
  display: grid;
  grid-template-columns: var(--thumb-size) 1fr auto auto;
  grid-template-areas: 'thumb info size actions';
  align-items: center;
  gap: var(--space-2) var(--space-3);
  padding: var(--space-2) var(--space-3);

  &.is-deleting {
    opacity: 0.5;
  }
  &.is-selected {
    background-color: var(--bg-selected);
    border-radius: var(--radius-md);
  }
  &.is-selected:hover {
    background-color: var(--bg-selected-hover);
  }
}

@container (max-width: 400px) {
  .af {
    grid-template-columns: var(--thumb-size) 1fr auto;
    grid-template-areas:
      'thumb info    info'
      '.     size    actions';
  }
  .af-actions {
    justify-content: flex-end;
  }
}

/* ── Areas ───────────────────────────────────────────── */
.af-thumb {
  grid-area: thumb;
}
.af-info {
  grid-area: info;
  min-width: 0;
}
.af-size {
  grid-area: size;
  font-size: var(--text-xs);
  font-weight: var(--font-semibold);
  white-space: nowrap;
}
.af-actions {
  grid-area: actions;
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

/* ── Thumbnail ───────────────────────────────────────── */
.af-thumb {
  figure {
    width: var(--thumb-size);
    height: var(--thumb-size);
    margin: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-sm);
    color: var(--gray-400);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .selectable {
    position: relative;
    cursor: pointer;
    border: 3px solid transparent;
    border-radius: var(--radius-sm);
    overflow: hidden;
  }

  .checkmark {
    visibility: hidden;
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: var(--text-lg);
    font-weight: var(--font-bold);
  }

  .selectable:hover,
  .selectable.is-selected {
    figure {
      opacity: 0.5;
    }
    .checkmark {
      visibility: visible;
    }
  }

  .selectable:hover {
    border-color: var(--bg-default);
    color: var(--bg-default);
  }

  .selectable.is-selected {
    border-color: var(--brand);
    color: var(--brand);
  }
}

/* ── Hover preview (rendered in portal — needs :global) ── */
:global(.thumb-preview) {
  display: block;
  width: 200px;
  height: 200px;
  object-fit: cover;
  background: var(--bg-base);
  border: 1px solid var(--bg-default);

  &.is-loading {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

/* ── File info ───────────────────────────────────────── */
.af-name {
  display: flex;
  align-items: baseline;
  gap: var(--space-1);
  min-width: 0;

  a {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
}

.af-meta {
  font-size: var(--text-xs);
  color: var(--text-muted);
}

/* ── Delete confirmation dropdown (rendered in portal) ── */
:global(.af-confirm) {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding: var(--space-2);
  max-width: 220px;
  font-size: var(--text-sm);

  .button {
    align-self: flex-end;
  }
}

/* ── Preview modal ───────────────────────────────────── */
.af-preview-body {
  padding: var(--space-2);
  height: 100%;
}
</style>
